import { useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { Upload, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { UPLOAD_CONSTRAINTS } from '../utils/fileValidation';

export interface UploadDropzoneProps {
  onFilesAccepted: (files: File[]) => void;
  onFilesRejected?: (rejections: FileRejection[]) => void;
  disabled?: boolean;
  maxFiles?: number;
}

export function UploadDropzone({
  onFilesAccepted,
  onFilesRejected,
  disabled = false,
  maxFiles = 1,
}: UploadDropzoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        onFilesAccepted(acceptedFiles);
      }
      if (rejectedFiles.length > 0 && onFilesRejected) {
        onFilesRejected(rejectedFiles);
      }
    },
    [onFilesAccepted, onFilesRejected]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf'],
    },
    maxSize: UPLOAD_CONSTRAINTS.maxSizeBytes,
    maxFiles,
    disabled,
    multiple: maxFiles > 1,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer',
        isDragActive && !isDragReject && 'border-primary bg-primary/5 scale-[1.01]',
        isDragReject && 'border-destructive bg-destructive/5',
        !isDragActive && !isDragReject && 'border-muted-foreground/25 hover:border-primary/50',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <input {...getInputProps()} />

      <div className="space-y-4">
        <Upload
          className={cn(
            'h-12 w-12 mx-auto',
            isDragActive && !isDragReject && 'text-primary',
            isDragReject && 'text-destructive',
            !isDragActive && 'text-muted-foreground'
          )}
        />

        {isDragReject ? (
          <div>
            <p className="font-medium text-destructive">Invalid file type</p>
            <p className="text-sm text-muted-foreground">
              Please upload .xlsx, .xls, .docx, or .pdf files only
            </p>
          </div>
        ) : isDragActive ? (
          <div>
            <p className="font-medium text-primary">Drop your file here</p>
            <p className="text-sm text-muted-foreground">Release to upload</p>
          </div>
        ) : (
          <div>
            <p className="font-medium">Drag & drop your DDQ file here</p>
            <p className="text-sm text-muted-foreground">or click to browse</p>
          </div>
        )}

        {!isDragActive && (
          <>
            <Button variant="outline" size="sm" disabled={disabled}>
              <FileText className="h-4 w-4 mr-2" />
              Select File
            </Button>
            <p className="text-xs text-muted-foreground">
              Supported formats: {UPLOAD_CONSTRAINTS.acceptedExtensions.join(', ')} (max{' '}
              {UPLOAD_CONSTRAINTS.maxSizeMB}MB)
            </p>
          </>
        )}
      </div>
    </div>
  );
}
